/**
 * entitlements — pure entitlement logic shared by the store and the Store UI.
 *
 * Kept free of any store/React dependency so it can be reasoned about and tested
 * in isolation. The store (useUserStore.isModuleEntitled) supplies the current
 * tier + per-module purchase flags and delegates the decision here.
 *
 * Rules (SHARED contract):
 *   - Un-catalogued id      → entitled (true). Never break ids the catalog doesn't know.
 *   - access:'free'         → entitled (true), always. (All real modules today.)
 *   - access:'paid'         → entitled iff the user's tier rank ≥ requiredTier rank,
 *                             OR a per-module purchase flag is set (SKU ownership).
 */
import { TIER_RANK, getCatalogEntry } from '../config/moduleCatalog';

const UNKNOWN_TIER_RANK = TIER_RANK.free; // treat unknown tiers as the lowest (free)

/** Rank a tier string; unknown/undefined tiers fall back to free (0). */
export function tierRank(tier) {
    return TIER_RANK[tier] ?? UNKNOWN_TIER_RANK;
}

/**
 * Decide entitlement for a single catalog entry given the user's tier and any
 * per-module purchase flags (map of moduleId -> truthy).
 *
 * @param {object|undefined} entry           catalog entry (from getCatalogEntry)
 * @param {string}           tier            user's current tier ('free' by default)
 * @param {object}           [purchasedFlags] per-module SKU ownership map
 * @returns {boolean}
 */
export function isEntitled(entry, tier, purchasedFlags = {}) {
    // Un-catalogued modules are always entitled (don't break unknown ids).
    if (!entry) return true;
    // Free modules are always entitled.
    if (entry.access !== 'paid') return true;
    // Per-module purchase (SKU ownership) grants access regardless of tier.
    if (entry.id && purchasedFlags[entry.id]) return true;
    // Otherwise require the tier rank to meet the module's requiredTier.
    const needed = entry.requiredTier ? tierRank(entry.requiredTier) : 0;
    return tierRank(tier) >= needed;
}

/**
 * Convenience: resolve a moduleId to its entitlement in one call.
 * Looks the entry up in the catalog, then applies isEntitled.
 */
export function isModuleIdEntitled(moduleId, tier, purchasedFlags = {}) {
    return isEntitled(getCatalogEntry(moduleId), tier, purchasedFlags);
}
